import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody } from 'reactstrap';

/**
 * NeoStatCard
 * Componente premium de exibição de estatísticas e indicadores do NeoApp.
 * Suporta três variantes visuais exclusivas do padrão outonal:
 *  - 'contour': Cartão contornado com borda de 2px chocolate e ícone tátil.
 *  - 'soft': Cartão plano minimalista com fundo pastel creme.
 *  - 'gradient': Cartão herói de alto impacto com fundo gradiente chocolate e letras brancas.
 */
const NeoStatCard = ({
    title,
    value,
    icon, // e.g. 'mdi-account-multiple'
    variant = 'contour', // 'contour' | 'soft' | 'gradient'
    trend = null, // e.g. '+12%' ou '-3%'
    trendDirection = 'neutral', // 'up' | 'down' | 'neutral'
    trendLabel = '', // e.g. 'vs semana anterior'
    className = '',
    style = {},
    ...props
}) => {
    // Determina a classe correspondente ao estilo do cartão
    const cardClass = [
        'neo-stat-card-base',
        `neo-stat-card-${variant}`,
        className
    ].join(' ');

    // Renderização do badge de tendência
    const renderTrend = () => {
        if (!trend) return null;

        const trendClasses = {
            up: 'neo-stat-card-trend-badge neo-stat-card-trend-up',
            down: 'neo-stat-card-trend-badge neo-stat-card-trend-down',
            neutral: 'neo-stat-card-trend-badge neo-stat-card-trend-neutral'
        };

        const trendIcons = {
            up: 'mdi-arrow-up',
            down: 'mdi-arrow-down',
            neutral: 'mdi-minus'
        };

        const activeClass = trendClasses[trendDirection] || trendClasses.neutral;
        const activeIcon = trendIcons[trendDirection] || trendIcons.neutral;

        return (
            <div className="d-flex align-items-center gap-1.5 mt-2 flex-wrap">
                <span className={activeClass}>
                    <i className={`mdi ${activeIcon}`}></i>
                    {trend}
                </span>
                {trendLabel && (
                    <span className="neo-stat-card-trend-label">
                        {trendLabel}
                    </span>
                )}
            </div>
        );
    };

    return (
        <Card className={cardClass} style={style} {...props}>
            <CardBody className="p-3.5">
                <div className="d-flex align-items-center justify-content-between">
                    {/* Textos à esquerda */}
                    <div className="flex-grow-1 min-width-0">
                        {title && (
                            <h6 className="neo-stat-card-title text-truncate">
                                {title}
                            </h6>
                        )}
                        {value !== undefined && (
                            <h3 className="neo-stat-card-value">
                                {value}
                            </h3>
                        )}
                    </div>

                    {/* Ícone à direita */}
                    {icon && (
                        <div className="neo-stat-card-icon-bg ms-3">
                            <i className={`mdi ${icon} font-size-22`}></i>
                        </div>
                    )}
                </div>

                {/* Linha de Tendência */}
                {renderTrend()}
            </CardBody>
        </Card>
    );
};

NeoStatCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    icon: PropTypes.string,
    variant: PropTypes.oneOf(['contour', 'soft', 'gradient']),
    trend: PropTypes.string,
    trendDirection: PropTypes.oneOf(['up', 'down', 'neutral']),
    trendLabel: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object
};

export default NeoStatCard;
